import React, { useState, useEffect, useMemo, useRef } from 'react';
import { ChevronDown } from 'lucide-react';
import { 
  getSystemFonts, 
  filterFonts, 
  createFontOption,
  FONT_CATEGORIES 
} from '../../utils/fontUtils';

/**
 * FontPicker Component
 * A font selector that loads available fonts from the server
 * Ensures consistency between client selection and server rendering capabilities
 * Compatible with After Effects (OpenType and TrueType fonts only)
 * 
 * @param {string} value - Current After Effects font name (e.g., "SegoeUI-Bold")
 * @param {function} onChange - Callback when font changes (receives AE font name)
 * @param {string} className - Additional CSS classes
 */
const FontPicker = ({ value, onChange, className = '', dropDirection = 'down', triggerOnly = false, isOpen: externalIsOpen, onToggle }) => {
  const [fonts, setFonts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [internalIsOpen, setInternalIsOpen] = useState(false);
  const isOpen = externalIsOpen !== undefined ? externalIsOpen : internalIsOpen;
  const setIsOpen = onToggle || setInternalIsOpen;
  const dropdownRef = useRef(null);
  const panelRef = useRef(null);
  const triggerRef = useRef(null);
  const [dropdownPos, setDropdownPos] = useState(null);

  // Calculate fixed position when dropdown opens so it escapes overflow containers
  useEffect(() => {
    if (!isOpen) { setDropdownPos(null); return; }
    const anchor = triggerRef.current;
    if (!anchor) return;
    const rect = anchor.getBoundingClientRect();
    const dropWidth = Math.max(rect.width, 200);
    if (dropDirection === 'up') {
      setDropdownPos({ bottom: window.innerHeight - rect.top + 4, left: rect.left, width: dropWidth });
    } else {
      setDropdownPos({ top: rect.bottom + 4, left: rect.left, width: dropWidth });
    }
  }, [isOpen, dropDirection]);

  // Load fonts from server on mount
  useEffect(() => {
    const loadFonts = async () => {
      setLoading(true);
      try {
        // Load fonts from server - ensures consistency with backend
        const serverFonts = await getSystemFonts();
        
        // Additional client-side filtering for safety
        const cleanFonts = serverFonts.filter(font => {
          // Filter out any fonts with encoding issues
          return font.name && 
                 font.aeFontName &&
                 !font.name.includes('?') &&
                 !font.aeFontName.includes('?') &&
                 font.name.length < 100; // Reasonable name length
        });
        
        // Sort fonts alphabetically by name
        const sortedFonts = [...cleanFonts].sort((a, b) => a.name.localeCompare(b.name));
        setFonts(sortedFonts);
        
        console.log(`Loaded ${sortedFonts.length} fonts from server`);
      } catch (error) {
        console.error('Error loading fonts from server:', error);
        setFonts([]);
      } finally {
        setLoading(false);
      }
    };

    loadFonts();
  }, []);

  // Get current font display info
  const currentFont = useMemo(() => {
    if (!value) return null;
    
    // Try to find in loaded fonts by AE font name first
    const found = fonts.find(f => f.aeFontName === value || f.fontFamily === value);
    if (found) return found;
    
    // If not found, create a custom option for the current value
    return createFontOption(value);
  }, [value, fonts]);

  // Filter fonts based on search
  const filteredFonts = useMemo(() => {
    return filterFonts(fonts, searchTerm);
  }, [fonts, searchTerm]);

  // Group fonts by category (only system fonts from server now)
  const groupedFonts = useMemo(() => {
    const groups = { system: [] };
    
    filteredFonts.forEach(font => {
      // All fonts from server are categorized as 'system'
      groups.system.push(font);
    });
    
    return groups;
  }, [filteredFonts]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current && !dropdownRef.current.contains(event.target) &&
        panelRef.current && !panelRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleFontSelect = (font) => {
    // Send the After Effects compatible font name
    onChange(font.aeFontName || font.fontFamily);
    setIsOpen(false);
    setSearchTerm('');
  };

  if (loading) {
    if (triggerOnly) return null;
    return (
      <button
        disabled
        className={`${className} px-3 py-2 text-sm rounded-lg bg-ag-black-800 border border-ag-black-400
                    opacity-50 cursor-not-allowed flex items-center justify-between text-white w-full`}
      >
        <span>Loading fonts...</span>
        <ChevronDown size={16} className="ml-2 flex-shrink-0" />
      </button>
    );
  }

  return (
    <div className="relative" ref={(el) => { dropdownRef.current = el; triggerRef.current = el; }}>
      {/* Custom dropdown button matching existing style */}
      {!triggerOnly && (
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className={`${className} px-3 py-2 text-sm rounded-lg bg-ag-black-800 border border-ag-black-400
                      hover:bg-ag-black-600 transition-colors flex items-center justify-between
                      text-white w-full`}
          style={{
            fontFamily: currentFont?.fontFamily || 'inherit',
            fontWeight: currentFont?.weight || 'normal',
            fontStyle: currentFont?.style || 'normal',
            fontSize: '14px' // Fixed font size to prevent huge fonts in button
          }}
        >
          <span className="truncate">
            {currentFont ? currentFont.name : 'Select Font'}
          </span>
          <ChevronDown
            size={16}
            className={`transition-transform ml-2 flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`}
          />
        </button>
      )}
      
      {/* Custom dropdown menu matching existing style */}
      {isOpen && dropdownPos && (
        <div
          ref={panelRef}
          className={`fixed z-[9999] min-w-[200px] max-w-[400px] bg-ag-black-800 border border-ag-black-400
                        rounded-lg shadow-lg max-h-80 overflow-y-auto overflow-x-hidden animate-in fade-in ${dropDirection === 'up' ? 'slide-in-from-bottom-1' : 'slide-in-from-top-1'}`}
          style={{ ...dropdownPos, scrollbarWidth: 'thin', scrollbarColor: '#4A4A4A #1F1E1E' }}
        >
          {/* Search input */}
          <div className="sticky top-0 bg-ag-black-800 p-2 border-b border-ag-black-400">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search fonts..."
              className="w-full px-3 py-1.5 bg-ag-black-700 text-white text-sm rounded 
                         border border-ag-black-400 focus:outline-none focus:border-ag-green 
                         placeholder-ag-black-300"
              onClick={(e) => e.stopPropagation()}
              autoFocus
            />
          </div>
          
          {/* Font options */}
          <div className="py-1">
            {/* Current font if not in list */}
            {currentFont && currentFont.category === 'custom' && (
              <>
                <div className="px-4 py-1.5 text-xs text-ag-black-300 uppercase tracking-wider font-medium">
                  {FONT_CATEGORIES.custom}
                </div>
                <button
                  onClick={() => handleFontSelect(currentFont)}
                  className="w-full text-left px-4 py-2 text-sm text-white 
                             hover:bg-ag-black-600 hover:text-ag-green transition-colors
                             overflow-hidden"
                  style={{ 
                    fontFamily: currentFont.fontFamily,
                    fontWeight: currentFont.weight || 'normal',
                    fontStyle: currentFont.style || 'normal'
                  }}
                >
                  <span className="block truncate pr-2">{currentFont.name}</span>
                  <span className="text-xs text-ag-black-300 truncate block">AE: {currentFont.aeFontName}</span>
                </button>
              </>
            )}
            
            {/* Server Fonts */}
            {groupedFonts.system.length > 0 && (
              <>
                <div className="px-4 py-1.5 text-xs text-ag-black-300 uppercase tracking-wider font-medium 
                                {currentFont && currentFont.category === 'custom' ? 'border-t border-ag-black-400 mt-1' : ''}">
                  {FONT_CATEGORIES.system}
                </div>
                {groupedFonts.system.map(font => (
                  <button
                    key={font.aeFontName}
                    onClick={() => handleFontSelect(font)}
                    className={`w-full text-left px-4 py-2.5 text-sm text-white 
                               hover:bg-ag-black-600 hover:text-ag-green transition-colors
                               overflow-hidden
                               ${value === font.aeFontName ? 'bg-ag-black-600 text-ag-green' : ''}`}
                    style={{ 
                      fontFamily: font.fontFamily || 'inherit',
                      fontWeight: font.weight || 'normal',
                      fontStyle: font.style || 'normal',
                      fontSize: '14px', // Fixed font size to prevent huge fonts
                      lineHeight: '1.5' // Ensure consistent line height
                    }}
                    // title={`AE Font: ${font.aeFontName}`}
                  >
                    <span className="block truncate pr-2">{font.name}</span>
                    {font.aeFontName !== font.fontFamily && (
                      <span className="text-xs text-ag-black-300 opacity-75 truncate block">({font.aeFontName})</span>
                    )}
                  </button>
                ))}
              </>
            )}
            
                        
            {/* No results */}
            {filteredFonts.length === 0 && (
              <div className="px-4 py-3 text-sm text-ag-black-300 text-center">
                No fonts found
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default FontPicker;